import {Injectable, inject} from '@angular/core'
import type {NavigationExtras} from '@angular/router'
import type {ContactPerson} from '@cart/src/app/entities/modals/request-modals/contact-person.modal'
import {FirebaseMessaging} from '@capacitor-firebase/messaging'
import {type Subscription, firstValueFrom, of} from 'rxjs'
import {map} from 'rxjs/operators'
import {AppModeService} from '@services/app-mode.service'
import {BadgeNotificationService} from '@services/badge-notification.service'
import {AuthService} from './auth.service'
import {ChatListService} from './chat-list.service'
import {ConversationService} from './conversation.service'
import {FirebaseService} from './firebase.service'
import {InfoService} from './info.service'
import {NavigationService} from './navigation.service'
import {UserService} from './user.service'
import {LogType, Logger} from './utils/logger'

@Injectable({providedIn: 'root'})
export class NotificationService {
  readonly #conversationService = inject(ConversationService)
  readonly #userService = inject(UserService)
  readonly #infoService = inject(InfoService)
  readonly #chatListService = inject(ChatListService)
  readonly #authService = inject(AuthService)
  readonly #navigationService = inject(NavigationService)
  readonly #appModeService = inject(AppModeService)
  readonly #badgeNotificationService = inject(BadgeNotificationService)
  readonly #firebaseService = inject(FirebaseService)

  limit = 12
  offset = 0
  pagination = 0

  fcmToken: string
  private userSubscription: Subscription

  async init() {
    Logger.log(LogType.debug, 'Initializing notifications...')
    if (!this.#infoService.isWeb()) {
      const hasPermission = await this.hasNotificationPermission()
      if (!hasPermission) {
        await this.requestNotificationsPermission()
      }
      if (!this.fcmToken) {
        this.#firebaseService.getFcmToken().then(token => (this.fcmToken = token))
      }
      this.onTokenRefresh()
      this.onMessageReceivedListener()
      this.#badgeNotificationService.clearBadgeNumber()
    }
  }

  async hasNotificationPermission(): Promise<boolean> {
    const permissions = await FirebaseMessaging.checkPermissions()
    Logger.log(LogType.debug, 'notifications.service - Permission is ' + (permissions.receive ? 'granted' : 'denied'))

    return permissions.receive === 'granted'
  }

  async requestNotificationsPermission(): Promise<boolean> {
    const hasPermission = await FirebaseMessaging.requestPermissions()
    Logger.log(
      LogType.debug,
      'notifications.service - Grant permission is ' + (hasPermission.receive ? 'granted' : 'denied')
    )
    return hasPermission.receive === 'granted'
  }

  onMessageReceivedListener() {
    FirebaseMessaging.addListener('notificationActionPerformed', notificationData => {
      if (notificationData.notification) {
        this.#authService.disableAuthRedirect()
        this.onUserTapNotification(notificationData.notification.data).then()
      }
    })
  }

  async goToChat(notification): Promise<boolean> {
    const user = this.#userService.getUser()
    if (!!user && notification && (notification.userId || (notification.Mobile && notification.countryCode))) {
      await firstValueFrom(this.getChatList())
      let success = false
      const contact = this.#chatListService.findContact(
        notification.userId,
        notification.countryCode,
        notification.Mobile
      )
      if (contact) {
        const currentChatUser = this.#conversationService.getCurrentChatUser()
        if (currentChatUser?.distributor_code !== contact?.distributor_code) {
          this.#chatListService.closeConversation()
          this.#navigationService.navigateRoot('home', null, true)
        }
        success = await this.#chatListService.openConversation(contact, true, null, null, true)
      }
      return success
    }

    return false
  }

  redirectToChatList(extras: NavigationExtras = {}) {
    Logger.log(LogType.debug, 'notification.service - redirectToChatList()')
    this.#navigationService.navigateForward('home', extras)
  }

  getChatList() {
    const contacts = this.#chatListService.getContactsList()
    if (!!contacts && contacts.length > 0) {
      return of(null)
    }
    if (this.#appModeService.isB2C() && !this.#userService.isCurrentUserSeller()) {
      return this.#chatListService
        .getB2CProviderContacts(false)
        .pipe(map((contactList: ContactPerson[]) => contactList))
    } else {
      return this.#chatListService
        .getContactList(false, {}, true, this.offset, this.limit, this.pagination)
        .pipe(map((contactList: ContactPerson[]) => contactList))
    }
  }

  onTokenRefresh() {
    FirebaseMessaging.addListener('tokenReceived', tokenReceived => {
      Logger.log(LogType.debug, `notifications.service - Got a new token ${tokenReceived.token}`)
      if (tokenReceived) {
        this.fcmToken = tokenReceived.token
        this.userSubscription = this.#userService.getUserSubscription().subscribe(() => {
          this.#authService.initializeUserProfile()
          this.userSubscription.unsubscribe()
        })
      }
    })
  }

  private async onUserTapNotification(notificationData: any) {
    Logger.log(LogType.debug, `notification.service - User tap the notification`)
    let didGoToChat = false
    if (notificationData && notificationData.type) {
      didGoToChat = await this.goToChat(notificationData)
    }

    this.#authService.enableAuthRedirect()
    if (didGoToChat) {
      return
    }
    this.redirectToChatList()
  }
}
